import { useLayoutEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { registerProtectedRoute } from '@/lib/api/core';
import { useSessionHeartbeat } from '@/hooks/useSessionHeartbeat';
import { useAuthStore } from '@/store/authStore';
import { useDemoModeStore } from '@/store/demoModeStore';
import { toDemoPath } from '@/lib/demo/coverage';

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  const { isDemoMode } = useDemoModeStore();
  const location = useLocation();

  // Session heartbeat for visit tracking (only when authenticated)
  useSessionHeartbeat();

  // Marks this page as requiring a session, so an unrecoverable 401 sends the
  // visitor to /login here and ONLY here - a public page instead signs out in
  // place. A layout effect because it must be registered before any child's
  // passive effect can start a request.
  useLayoutEffect(() => registerProtectedRoute(), []);

  if (!isAuthenticated) {
    // A visitor exploring the public demo is not a lapsed session - they never
    // had one. Send them to the demo's own version of the page rather than to
    // a sign-in form for an account they do not have.
    //
    // This is the right layer for it, and the only one that holds. Rewriting
    // links component by component works until someone adds a link: /demo
    // renders the whole authenticated app, so the in-page drilldowns alone
    // (developer cards, "View velocity", stuck-PR rows) reach fifteen distinct
    // protected paths, and the sidebar four more render sites. ProtectedRoute
    // already owns the "you cannot be here" decision, so deciding it correctly
    // once covers every link that exists now and every one added later.
    if (isDemoMode) {
      return <Navigate to={toDemoPath(location.pathname)} replace />;
    }

    // Capture the current path and search params to redirect back after login
    const currentPath = location.pathname + location.search;
    const returnUrl = encodeURIComponent(currentPath);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  return <>{children}</>;
}
